// Pre-Approval milestones — Section 4 (Final Approval to Underwriting).
// Replaces the legacy "Certification submitted" sub-card. Hosts the
// metrics form, the LA's three attestation checkboxes, and the single
// Send-to-Underwriting action that atomically writes the certification,
// stamps the deal's submission timestamp, and advances the deal to
// Underwriting / AssignedToA1 via the existing assignment service.

import { useEffect, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Badge, Button, Card, FormField, Input } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { useA1Picker } from "@/hooks/deals/useDealLookups";
import { useDateFormatter } from "@/utils/datetime";
import {
  usePreApprovalReadiness,
  useSubmitPreApproval,
} from "@/hooks/deals/usePreApproval";
import {
  PREAPPROVAL_PERMISSIONS,
  type CertificationRecommendation,
  type SuggestedCreditRatingPreliminary,
} from "@/types/deals";

interface Props {
  dealId: string;
}

interface FormState {
  dscr: string;
  rating: SuggestedCreditRatingPreliminary | "";
  recommendation: CertificationRecommendation | "";
  keyFindings: string;
  conditions: string;
  attestMeetsRequirements: boolean;
  attestDocsComplete: boolean;
  attestApplicationsInOrder: boolean;
  assignedA1UserId: string;
}

const EMPTY: FormState = {
  dscr: "",
  rating: "",
  recommendation: "ApproveForUW",
  keyFindings: "",
  conditions: "",
  attestMeetsRequirements: false,
  attestDocsComplete: false,
  attestApplicationsInOrder: false,
  assignedA1UserId: "",
};

export function PreApprovalFinalSubmissionSection({ dealId }: Props) {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canSubmit = hasPermission(PREAPPROVAL_PERMISSIONS.SubmitToUnderwriting);

  const readiness = usePreApprovalReadiness(dealId);
  const submit = useSubmitPreApproval(dealId);
  const a1Picker = useA1Picker();
  const { dateTime } = useDateFormatter();

  const [state, setState] = useState<FormState>(EMPTY);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const seeded = useRef(false);

  const isSubmitted = readiness.data?.isSubmitted ?? false;
  const milestonesReady =
    (readiness.data?.borrowerApplicationReceived ?? false) &&
    (readiness.data?.sellerApplicationReceived ?? false) &&
    (readiness.data?.borrowerLetterReceivedSigned ?? false) &&
    (readiness.data?.sellerLetterReceivedSigned ?? false) &&
    (readiness.data?.allRequiredDocumentsCleared ?? false);

  const allAttested =
    state.attestMeetsRequirements &&
    state.attestDocsComplete &&
    state.attestApplicationsInOrder;

  const blockerTooltip = pickBlocker({
    canSubmit,
    isSubmitted,
    milestonesReady,
    readiness: readiness.data,
    allAttested,
    a1Selected: !!state.assignedA1UserId,
    recommendationSelected: !!state.recommendation,
  });

  const sendDisabled =
    !canSubmit ||
    isSubmitted ||
    !milestonesReady ||
    !allAttested ||
    !state.assignedA1UserId ||
    !state.recommendation ||
    submit.isPending;

  // Lock the form once submitted.
  useEffect(() => {
    if (isSubmitted) {
      setConfirmOpen(false);
    }
  }, [isSubmitted]);

  // Seed the locked form from the persisted submission on re-read. The
  // submit action is gated on all three attestations, so a submitted
  // deal had them all checked — seed them true for the locked display.
  useEffect(() => {
    const r = readiness.data;
    if (!r || !r.isSubmitted || seeded.current) return;
    seeded.current = true;
    setState({
      dscr: r.dscrPreliminary != null ? String(r.dscrPreliminary) : "",
      rating: r.suggestedCreditRating ?? "",
      recommendation: r.recommendation ?? "",
      keyFindings: r.keyFindings ?? "",
      conditions: r.conditionsText ?? "",
      attestMeetsRequirements: true,
      attestDocsComplete: true,
      attestApplicationsInOrder: true,
      assignedA1UserId: r.assignedA1UserId ?? "",
    });
  }, [readiness.data]);

  function patch<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((s) => ({ ...s, [key]: value }));
  }

  async function performSubmit() {
    try {
      await submit.mutateAsync({
        dscrPreliminary: state.dscr ? Number(state.dscr) : null,
        suggestedCreditRating: state.rating || null,
        recommendation: state.recommendation || null,
        keyFindings: state.keyFindings.trim() || null,
        conditionsText: state.conditions.trim() || null,
        dealMeetsRequirements: state.attestMeetsRequirements,
        allDocumentsComplete: state.attestDocsComplete,
        applicationsInOrder: state.attestApplicationsInOrder,
        assignedA1UserId: state.assignedA1UserId,
      });
      toast.success("Sent to Underwriting.");
      setConfirmOpen(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.info(err.errors.join("; ") || "Already submitted.");
        setConfirmOpen(false);
        return;
      }
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Could not send to Underwriting.",
      );
    }
  }

  const headerBadge: { label: string; tone: "draft" | "review" | "finalized" } =
    isSubmitted
      ? { label: "Submitted", tone: "finalized" }
      : milestonesReady
        ? { label: "Ready", tone: "review" }
        : { label: "Pending", tone: "draft" };

  return (
    <Card>
      <div className="flex items-start justify-between flex-wrap gap-space-2 mb-space-3">
        <div>
          <div className="font-display font-bold text-h3 text-onyx">
            Section 4 — Submit to Underwriting
          </div>
          <p className="text-small text-gray-mid m-0">
            Confirm the deal is ready and send it to Underwriting. This action
            advances the deal stage and reassigns it to A1.
          </p>
        </div>
        <Badge status={headerBadge.tone}>{headerBadge.label}</Badge>
      </div>

      {isSubmitted && readiness.data?.submittedAt && (
        <div className="mb-space-3 flex items-center gap-space-2 text-small text-status-success">
          <CheckCircle2 size={16} aria-hidden />
          <span>
            Submitted {dateTime(readiness.data.submittedAt)}
            {readiness.data.submittedByDisplayName
              ? ` by ${readiness.data.submittedByDisplayName}`
              : ""}
            .
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
        <FormField label="DSCR (preliminary)">
          <Input
            type="number"
            step="0.01"
            value={state.dscr}
            disabled={isSubmitted}
            onChange={(e) => patch("dscr", e.target.value)}
          />
        </FormField>
        <FormField label="Suggested credit rating">
          <Select
            value={state.rating}
            disabled={isSubmitted}
            onChange={(e) =>
              patch("rating", e.target.value as SuggestedCreditRatingPreliminary | "")
            }
          >
            <option value="">Select…</option>
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="C">C</option>
            <option value="D">D</option>
            <option value="E">E</option>
            <option value="TooEarly">Too early</option>
          </Select>
        </FormField>
        <FormField label="Recommendation" required>
          <Select
            value={state.recommendation}
            disabled={isSubmitted}
            onChange={(e) =>
              patch("recommendation", e.target.value as CertificationRecommendation | "")
            }
          >
            <option value="">Select…</option>
            <option value="ApproveForUW">Approve for Underwriting</option>
            <option value="ApproveWithConditions">Approve with conditions</option>
          </Select>
        </FormField>
        <FormField label="Assign A1 underwriter" required>
          <Select
            value={state.assignedA1UserId}
            disabled={isSubmitted || a1Picker.isLoading}
            onChange={(e) => patch("assignedA1UserId", e.target.value)}
          >
            <option value="">
              {a1Picker.isLoading ? "Loading users…" : "Select an A1…"}
            </option>
            {(a1Picker.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>{u.displayName}</option>
            ))}
            {isSubmitted &&
              state.assignedA1UserId &&
              !(a1Picker.data ?? []).some((u) => u.id === state.assignedA1UserId) && (
                <option value={state.assignedA1UserId}>
                  {readiness.data?.assignedA1DisplayName ?? "Assigned A1"}
                </option>
              )}
          </Select>
        </FormField>
      </div>

      {state.recommendation === "ApproveWithConditions" && (
        <div className="mt-space-3">
          <FormField label="Conditions" required>
            <Textarea
              rows={3}
              value={state.conditions}
              disabled={isSubmitted}
              onChange={(e) => patch("conditions", e.target.value)}
            />
          </FormField>
        </div>
      )}

      <div className="mt-space-3">
        <FormField label="Key findings">
          <Textarea
            rows={3}
            value={state.keyFindings}
            disabled={isSubmitted}
            onChange={(e) => patch("keyFindings", e.target.value)}
          />
        </FormField>
      </div>

      <div className="mt-space-4 border-t border-border-light pt-space-3">
        <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
          Attestations
        </div>
        <div className="flex flex-col gap-space-2">
          <CheckboxRow
            checked={state.attestMeetsRequirements}
            disabled={isSubmitted}
            onChange={(v) => patch("attestMeetsRequirements", v)}
            label="I confirm the deal meets our requirements."
          />
          <CheckboxRow
            checked={state.attestDocsComplete}
            disabled={isSubmitted}
            onChange={(v) => patch("attestDocsComplete", v)}
            label="I confirm all documents are complete."
          />
          <CheckboxRow
            checked={state.attestApplicationsInOrder}
            disabled={isSubmitted}
            onChange={(v) => patch("attestApplicationsInOrder", v)}
            label="I confirm the applications look totally in order."
          />
        </div>
      </div>

      <div className="mt-space-4 flex items-center justify-end gap-space-2">
        <Button
          variant="primary"
          onClick={() => setConfirmOpen(true)}
          disabled={sendDisabled}
          loading={submit.isPending}
          title={blockerTooltip ?? undefined}
        >
          Send to Underwriting →
        </Button>
      </div>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Send to Underwriting?"
        description="This advances the deal stage and assigns it to A1. The action is captured in the audit log and can only be reversed by an admin."
        footer={
          <>
            <Button
              variant="tertiary"
              onClick={() => setConfirmOpen(false)}
              disabled={submit.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void performSubmit()}
              loading={submit.isPending}
            >
              Confirm
            </Button>
          </>
        }
      >
        <p className="text-small text-onyx m-0">
          You are about to send this deal to Underwriting and reassign it to
          the selected A1.
        </p>
      </Modal>
    </Card>
  );
}

function CheckboxRow({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-start gap-space-2 text-small text-onyx cursor-pointer">
      <input
        type="checkbox"
        className="mt-[3px]"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

function pickBlocker(args: {
  canSubmit: boolean;
  isSubmitted: boolean;
  milestonesReady: boolean;
  readiness: ReturnType<typeof usePreApprovalReadiness>["data"];
  allAttested: boolean;
  a1Selected: boolean;
  recommendationSelected: boolean;
}): string | null {
  if (!args.canSubmit) return "You don't have permission to submit.";
  if (args.isSubmitted) return "Already submitted to Underwriting.";
  const r = args.readiness;
  if (r) {
    if (!r.borrowerApplicationReceived) return "Borrower application has not been marked Received.";
    if (!r.sellerApplicationReceived) return "Seller application has not been marked Received.";
    if (!r.borrowerLetterReceivedSigned) return "Borrower letter has not been marked Received-Signed.";
    if (!r.sellerLetterReceivedSigned) return "Seller letter has not been marked Received-Signed.";
    if (!r.allRequiredDocumentsCleared)
      return `${r.outstandingRequiredDocuments} required document(s) outstanding.`;
  } else if (!args.milestonesReady) {
    return "Milestone prerequisites not met.";
  }
  if (!args.recommendationSelected) return "Pick a recommendation.";
  if (!args.a1Selected) return "Pick an A1 underwriter.";
  if (!args.allAttested) return "Check all three attestations.";
  return null;
}
